// AvvisoSospensione.tsx — quando il server dice «sospeso» (v021, scelte legali 2 e 7): perché, se è automatica o decisa da
// una persona, fino a quando, il codice del caso e «Chiedi a una persona di ricontrollare» (email già scritta con il
// codice). Il dettaglio arriva con l'errore (accesso) o si chiede al server con l'id del telefono.
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { DettaglioSospensione } from "../../../condiviso/sospensione.ts";
import { richiesta } from "../servizi/api.ts";
import { idDispositivo } from "../servizi/dispositivo.ts";
import { BASE_API, PERCORSO_REGOLE } from "../costanti.ts";

interface Dati {
  sospensione: DettaglioSospensione | null;
  email: string | null;
}

/** Il dettaglio dentro un errore del server (ErroreRete.dettagli), se c'è. */
export function datiSospensione(dettagli: Record<string, unknown> | undefined): Dati | null {
  const s = dettagli?.sospensione as DettaglioSospensione | undefined;
  if (!s || typeof s !== "object" || typeof s.codice !== "string") return null;
  return { sospensione: s, email: typeof dettagli?.email === "string" ? dettagli.email : null };
}

export function AvvisoSospensione({ dati, chi = "dispositivo", seNo }: { dati?: Dati | null; chi?: "dispositivo" | "account"; seNo?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [d, setD] = useState<Dati | null>(dati ?? null);
  const [caricato, setCaricato] = useState(!!dati);
  useEffect(() => {
    if (dati) return;
    let vivo = true;
    richiesta<Dati>(`/api/v1/sospensione?dispositivo=${encodeURIComponent(idDispositivo())}`)
      .then((x) => vivo && setD(x))
      .catch(() => undefined)
      .finally(() => vivo && setCaricato(true));
    return () => {
      vivo = false;
    };
  }, [dati]);

  const s = d?.sospensione ?? null;
  // «bloccato» può voler dire anche «bloccato da chi è dentro»: se il telefono non è sospeso, il messaggio di sempre.
  if (seNo !== undefined && !caricato) return null;
  if (seNo !== undefined && !s) return <>{seNo}</>;
  const tipo = s?.chi ?? chi;
  const data = s?.fino ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "long", timeStyle: "short" }).format(new Date(s.fino)) : null;
  const mail =
    s && d?.email
      ? `mailto:${d.email}?subject=${encodeURIComponent(t("sospensione.email_oggetto", { codice: s.codice }))}&body=${encodeURIComponent(
          `${t("sospensione.email_testo", { codice: s.codice })}\n\n`,
        )}`
      : null;
  return (
    <div className="ld-sospensione" role="alert" aria-busy={!caricato}>
      <p className="ld-sospensione-titolo">{t(tipo === "account" ? "sospensione.account" : "sospensione.dispositivo")}</p>
      {s && (
        <>
          <p>{t("sospensione.motivo", { motivo: t(`sospensione.motivo_${s.motivo}`) })}</p>
          {s.nota && <p className="ld-sospensione-nota">«{s.nota}»</p>}
          <p className="nota">{t(s.automatica ? "sospensione.automatica" : "sospensione.decisa")}</p>
          {data && <p className="nota">{t("sospensione.fino", { data })}</p>}
          <p className="ld-sospensione-codice">
            {t("sospensione.codice", { codice: "" })}
            <strong translate="no">{s.codice}</strong>
          </p>
          <p className="nota">{t("sospensione.ricontrollo_testo")}</p>
          {mail && (
            <a className="ld-cta ld-cta-centrata ld-sospensione-email" href={mail}>
              {t("sospensione.ricontrollo")}
            </a>
          )}
        </>
      )}
      <a className="nota" href={`${BASE_API}${PERCORSO_REGOLE}?lingua=${encodeURIComponent(i18n.language)}`} target="_blank" rel="noopener">
        {t("sospensione.vedi_regole")}
      </a>
    </div>
  );
}
